/**
 * BookAppointmentEditor
 *
 * Workflow node that books a meeting on Outlook Calendar at a specific point
 * in the conversation — no HTTP node configuration required.
 *
 * Requires:
 *   1. Microsoft 365 connected (Integrations → Microsoft 365)
 *   2. Outlook Calendar configured (Integrations → Outlook Calendar)
 *   3. Visitor name + email + preferred time collected via Ask Question nodes BEFORE this node
 *
 * The node stores the booking result (eventId, confirmationMessage) in workflow variables
 * so the next Send Message node can confirm the booking to the visitor.
 *
 * Execution: handled in velaro-messaging WorkflowJobRunner — calls
 *   POST /api/admin/OutlookCalendar/Book  with the configured variable values.
 *
 * Fallback: if the messaging engine does not yet support this node type, replace it
 *   with an HTTP Request node (POST /api/admin/OutlookCalendar/Book).
 */
import * as React from "react";
import { Link } from "react-router-dom";
import { FormGroup } from "../RightAsideHelpers";
import { WorkflowNode } from "../../types";

interface Props {
  node: WorkflowNode;
  onUpdate: (node: Partial<WorkflowNode>) => void;
}

const VAR_HINT = (
  <span className="text-xs text-slate-400 block mt-1">
    Use <code className="bg-slate-100 px-1 rounded">{"<<variableName>>"}</code> syntax.
    Variables are set by <strong>Ask Question</strong> nodes earlier in this workflow.
  </span>
);

export default function BookAppointmentEditor({ node, onUpdate }: Props) {
  const nameVar         = node.bookApptNameVar  ?? "visitorName";
  const emailVar        = node.bookApptEmailVar ?? "visitorEmail";
  const timeVar         = node.bookApptTimeVar  ?? "preferredTime";
  const notesVar        = node.bookApptNotesVar ?? "";
  const durationOverride = node.bookApptDurationOverride ?? 0;
  const titleOverride   = node.bookApptTitleOverride ?? "";
  const resultVar       = node.bookApptResultVar ?? "bookingConfirmation";
  const eventIdVar      = node.bookApptEventIdVar ?? "bookingEventId";

  return (
    <>
      {/* Info banner */}
      <div className="mb-4 rounded-md bg-blue-50 border border-blue-200 px-3 py-2.5 text-xs text-blue-800">
        <p className="font-semibold mb-1">How this node works</p>
        <p className="leading-relaxed">
          At this point in the workflow, Velaro calls the Microsoft Graph API to create a calendar
          event on the configured organizer's Outlook Calendar and (optionally) sends a calendar
          invite to the visitor. The booking confirmation is saved to a workflow variable you can
          use in a Send Message node immediately after.
        </p>
        <p className="mt-1.5">
          <strong>Pre-requisite:</strong> Configure organizer email in{" "}
          <Link to="/integrations/outlook-calendar" className="underline font-medium">
            Integrations → Outlook Calendar
          </Link>.
        </p>
      </div>

      {/* Visitor info variables */}
      <div className="mb-4 rounded-md bg-slate-50 border border-slate-200 px-3 py-2.5">
        <p className="text-xs font-semibold text-slate-600 mb-2">Visitor info — from workflow variables</p>
        <p className="text-xs text-slate-500 mb-3 leading-relaxed">
          Add <strong>Ask Question</strong> nodes before this node to collect name, email, and
          preferred meeting time. Enter the variable names those nodes save to:
        </p>

        <FormGroup label="Visitor name variable">
          <input
            className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            value={nameVar}
            onChange={e => onUpdate({ bookApptNameVar: e.currentTarget.value })}
            placeholder="visitorName"
          />
          {VAR_HINT}
        </FormGroup>

        <FormGroup label="Visitor email variable">
          <input
            className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            value={emailVar}
            onChange={e => onUpdate({ bookApptEmailVar: e.currentTarget.value })}
            placeholder="visitorEmail"
          />
          {VAR_HINT}
        </FormGroup>

        <FormGroup label="Preferred time variable">
          <input
            className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            value={timeVar}
            onChange={e => onUpdate({ bookApptTimeVar: e.currentTarget.value })}
            placeholder="preferredTime"
          />
          <span className="text-xs text-slate-400 block mt-1">
            Should contain an ISO 8601 datetime or a parseable string like "Friday 2pm EST".
          </span>
        </FormGroup>

        <FormGroup label="Notes variable (optional)">
          <input
            className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            value={notesVar}
            onChange={e => onUpdate({ bookApptNotesVar: e.currentTarget.value })}
            placeholder="visitorNotes  (leave blank to omit)"
          />
        </FormGroup>
      </div>

      {/* Optional overrides */}
      <div className="mb-4 rounded-md bg-slate-50 border border-slate-200 px-3 py-2.5">
        <p className="text-xs font-semibold text-slate-600 mb-2">Optional overrides</p>
        <p className="text-xs text-slate-500 mb-3">Leave blank to use the defaults from Outlook Calendar config.</p>

        <FormGroup label="Duration (minutes)">
          <input
            type="number"
            min={15}
            max={480}
            className="w-32 border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            value={durationOverride || ""}
            onChange={e => onUpdate({ bookApptDurationOverride: Number(e.currentTarget.value) || 0 })}
            placeholder="30 (default)"
          />
        </FormGroup>

        <FormGroup label="Meeting title override">
          <input
            className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            value={titleOverride}
            onChange={e => onUpdate({ bookApptTitleOverride: e.currentTarget.value })}
            placeholder={`"Demo with" + visitor name  (from config)`}
          />
        </FormGroup>
      </div>

      {/* Result variables */}
      <div className="mb-4 rounded-md bg-green-50 border border-green-200 px-3 py-2.5">
        <p className="text-xs font-semibold text-green-800 mb-2">Result variables — saved after booking</p>
        <p className="text-xs text-green-700 mb-3 leading-relaxed">
          After the appointment is booked, the following variables are available for use in
          subsequent <strong>Send Message</strong> or <strong>Set Data</strong> nodes.
        </p>

        <FormGroup label="Confirmation message variable">
          <input
            className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            value={resultVar}
            onChange={e => onUpdate({ bookApptResultVar: e.currentTarget.value })}
            placeholder="bookingConfirmation"
          />
          <span className="text-xs text-slate-400 block mt-1">
            Human-readable booking confirmation — send it to the visitor via a Send Message node.
            Example: "Your demo is booked for Friday, March 7 at 2:00 PM UTC."
          </span>
        </FormGroup>

        <FormGroup label="Event ID variable">
          <input
            className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            value={eventIdVar}
            onChange={e => onUpdate({ bookApptEventIdVar: e.currentTarget.value })}
            placeholder="bookingEventId"
          />
          <span className="text-xs text-slate-400 block mt-1">
            The Outlook Calendar event ID. Save it as a contact property so cancellation
            and reminder workflows can reference it later.
          </span>
        </FormGroup>
      </div>

      {/* After this node hint */}
      <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2.5 text-xs text-amber-800">
        <p className="font-semibold mb-1">Recommended next steps</p>
        <ol className="list-decimal list-inside space-y-1 leading-relaxed">
          <li>Add a <strong>Send Message</strong> node after this one — use <code className="bg-amber-100 px-1 rounded">{`<<${resultVar}>>`}</code> as the message text.</li>
          <li>Add a <strong>Set Data</strong> node to save <code className="bg-amber-100 px-1 rounded">{`<<${eventIdVar}>>`}</code> as a contact property — needed for cancellation flows.</li>
          <li>Create a <strong>Rule</strong> that tags conversations with <code className="bg-amber-100 px-1 rounded">appointment-booked</code> and triggers a confirmation email campaign.</li>
        </ol>
      </div>
    </>
  );
}
